import React from 'react'
import { responsiveMedia } from "../../services/media";
import "./createProductModalStyles.css"
import { FiPlus } from "react-icons/fi"
// import { Input } from "@iq/models"
import { Input } from "../../modelsTesting"
import { LuUpload } from "react-icons/lu";
import { IoClose } from "react-icons/io5";
import { buildDefaultImageLabel, buildImageAltValue, createLocalImagePreview, extractImageUrl, normalizeSkuPart, readFileAsDataUrl } from "./imageUtils";

export default function SimpleVariant({
    attributes = [],
    productName = "",
    onSaveVariant = () => {},
    editingVariant = null,
    isEditing = false,
    onCancelEdit = () => {},
}) {

    const normalizedAttributes = React.useMemo(
        () => (Array.isArray(attributes) ? attributes : []),
        [attributes]
    );
    const [ attributeValues, setAttributeValues ] = React.useState({});
    const [ attributeColors, setAttributeColors ] = React.useState({});
    const [ variantSKU, setVariantSKU ] = React.useState("");
    const [ variantPrice, setVariantPrice ] = React.useState("");
    const [ variantCost, setVariantCost ] = React.useState("");
    const [ variantStock, setVariantStock ] = React.useState("");
    const [ variantWeight, setVariantWeight ] = React.useState("");
    const [ variantImages, setVariantImages ] = React.useState([]);
    const objectUrlsRef = React.useRef([]);

    const [ saveVariantErrors, setSaveVariantErrors ] = React.useState([]);

    const parseMoneyToCents = React.useCallback((value) => {
        const normalizedValue = String(value ?? "")
            .trim()
            .replace(/[^0-9.-]/g, "");

        if (!normalizedValue) {
            return NaN;
        }

        const parsedValue = Number(normalizedValue);
        if (Number.isNaN(parsedValue)) {
            return NaN;
        }

        return Math.round(parsedValue * 100);
    }, []);

    const formatCentsToMoney = React.useCallback((value) => {
        const parsedValue = Number(value);
        if (Number.isNaN(parsedValue)) {
            return "";
        }

        return (parsedValue / 100).toFixed(2);
    }, []);

    React.useEffect(() => {
        setAttributeValues((previousValues) => {
            const nextValues = { ...previousValues };
            let changed = false;

            normalizedAttributes.forEach((attribute) => {
                if (!(attribute in nextValues)) {
                    nextValues[attribute] = "";
                    changed = true;
                }
            });

            Object.keys(nextValues).forEach((key) => {
                if (!normalizedAttributes.includes(key)) {
                    delete nextValues[key];
                    changed = true;
                }
            });

            return changed ? nextValues : previousValues;
        });
    }, [normalizedAttributes]);

    const generatedSKU = React.useMemo(() => {
        const productPart = normalizeSkuPart(productName);
        const attributeParts = normalizedAttributes
            .map((attribute) => normalizeSkuPart(attributeValues[attribute]))
            .filter(Boolean);

        return [productPart, ...attributeParts].filter(Boolean).join("-");
    }, [attributeValues, normalizedAttributes, productName]);

    const hasAtLeastOneAttributeValue = React.useMemo(() => {
        if (normalizedAttributes.length === 0) {
            return true;
        }

        return normalizedAttributes.some((attribute) => String(attributeValues[attribute] || "").trim());
    }, [attributeValues, normalizedAttributes]);

    React.useEffect(() => {
        setVariantSKU((previousSku) => (previousSku === generatedSKU ? previousSku : generatedSKU));
    }, [generatedSKU]);

    React.useEffect(() => {
        setVariantImages((previousImages) => {
            let changed = false;
            const nextImages = previousImages.map((image, index) => {
                if (!image?.isAltAuto) {
                    return image;
                }

                const nextAltText = buildImageAltValue(generatedSKU, index + 1);
                if (image.altText !== nextAltText) {
                    changed = true;
                    return {
                        ...image,
                        altText: nextAltText,
                    };
                }

                return image;
            });

            return changed ? nextImages : previousImages;
        });
    }, [generatedSKU]);

    const revokeObjectUrls = React.useCallback(() => {
        objectUrlsRef.current.forEach((existingUrl) => {
            URL.revokeObjectURL(existingUrl);
        });
        objectUrlsRef.current = [];
    }, []);

    const buildLocalPreview = React.useCallback((nextFile) => {
        const preview = createLocalImagePreview(nextFile);
        if (!preview) {
            return null;
        }

        objectUrlsRef.current.push(preview.url);
        return preview;
    }, []);

    React.useEffect(() => {
        return () => {
            revokeObjectUrls();
        };
    }, [revokeObjectUrls]);

    React.useEffect(() => {
        if (!isEditing || !editingVariant) {
            return;
        }

        const editingAttributes = Array.isArray(editingVariant.Attributes)
            ? editingVariant.Attributes
            : Array.isArray(editingVariant.attributes)
                ? editingVariant.attributes
                : [];
        const attributeValueByName = editingAttributes.reduce((accumulator, attribute) => {
            const attributeName = String(attribute?.Name || attribute?.name || "").trim();
            accumulator[attributeName] = String(attribute?.Value || attribute?.value || "");
            return accumulator;
        }, {});

        setAttributeValues(
            normalizedAttributes.reduce((accumulator, attributeName) => {
                accumulator[attributeName] = attributeValueByName[attributeName] || "";
                return accumulator;
            }, {})
        );
        setAttributeColors(editingAttributes.reduce((colors, attribute) => {
            const name = String(attribute?.Name || attribute?.name || "").trim();
            if (name.toLowerCase() === "color") {
                colors[name] = attribute?.color_code ?? attribute?.ColorCode ?? attribute?.colorCode ?? "#000000";
            }
            return colors;
        }, {}));
        setVariantPrice(formatCentsToMoney(editingVariant?.Price || editingVariant?.price || 0));
        setVariantCost(formatCentsToMoney(editingVariant?.Cost || editingVariant?.cost || 0));
        setVariantStock(String(editingVariant?.Stock || editingVariant?.stock || 0));
        setVariantWeight(String(editingVariant?.Weight || editingVariant?.weight || 0));

        const editingImages = Array.isArray(editingVariant.Images)
            ? editingVariant.Images
            : Array.isArray(editingVariant.images)
                ? editingVariant.images
                : [];
        const editingUploadImages = Array.isArray(editingVariant?.UploadImages)
            ? editingVariant.UploadImages.filter((item) => item?.file instanceof File)
            : [];
        const hasPendingLocalUploads = editingUploadImages.length > 0;

        revokeObjectUrls();

        const existingImageItems = editingImages
            .map((image, index) => {
                const imageUrl = extractImageUrl(image);
                if (!imageUrl) {
                    return null;
                }

                // Avoid showing the same image twice when local uploads are already tracked.
                if (hasPendingLocalUploads && /^data:/i.test(imageUrl)) {
                    return null;
                }

                const providedAltText = String(image?.AltText || image?.alt_text || image?.altText || "").trim();

                return {
                    id: typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                        ? crypto.randomUUID()
                        : `existing-image-${index}-${imageUrl}`,
                    url: imageUrl,
                    source: "existing",
                    file: null,
                    altText: providedAltText || buildImageAltValue(generatedSKU, index + 1),
                    isAltAuto: providedAltText === "",
                };
            })
            .filter(Boolean);

        const localImageItems = editingUploadImages
            .map((uploadImageItem, index) => {
                const preview = buildLocalPreview(uploadImageItem.file);
                if (!preview) {
                    return null;
                }

                const uploadImageAltText = String(uploadImageItem?.altText || "").trim();

                return {
                    ...preview,
                    altText: uploadImageAltText || buildDefaultImageLabel(uploadImageItem.file, generatedSKU, existingImageItems.length + index + 1),
                    isAltAuto: false,
                };
            })
            .filter(Boolean);

        setVariantImages([...existingImageItems, ...localImageItems]);
    }, [buildLocalPreview, editingVariant, formatCentsToMoney, generatedSKU, isEditing, normalizedAttributes, revokeObjectUrls]);

    const resetForm = React.useCallback(() => {
        setAttributeColors({});
        setAttributeValues(
            normalizedAttributes.reduce((accumulator, attribute) => {
                accumulator[attribute] = "";
                return accumulator;
            }, {})
        );
        setVariantPrice("");
        setVariantCost("");
        setVariantStock("");
        setVariantWeight("");
        revokeObjectUrls();
        setVariantImages([]);
    }, [normalizedAttributes, revokeObjectUrls]);

    const handleImageFileChange = React.useCallback((event) => {
        const selectedFiles = Array.from(event?.target?.files || []).filter((file) => file instanceof File);
        if (selectedFiles.length === 0) {
            return;
        }

        setVariantImages((previousImages) => {
            const nextImages = [...previousImages];

            selectedFiles.forEach((file) => {
                const preview = buildLocalPreview(file);
                if (!preview) {
                    return;
                }

                const nextImageIndex = nextImages.length + 1;
                nextImages.push({
                    ...preview,
                    altText: buildDefaultImageLabel(file, generatedSKU, nextImageIndex),
                    isAltAuto: false,
                });
            });

            return nextImages;
        });

        if (event?.target) {
            event.target.value = "";
        }
    }, [buildLocalPreview, generatedSKU]);

    const handleRemoveImage = React.useCallback((imageIdToRemove) => {
        setVariantImages((previousImages) => {
            const previewToRemove = previousImages.find((preview) => preview.id === imageIdToRemove);
            if (previewToRemove?.source === "local" && previewToRemove?.url) {
                URL.revokeObjectURL(previewToRemove.url);
                objectUrlsRef.current = objectUrlsRef.current.filter((url) => url !== previewToRemove.url);
            }

            return previousImages.filter((preview) => preview.id !== imageIdToRemove);
        });
    }, []);

    const handleImageAltTextChange = React.useCallback((imageIdToUpdate, nextAltText) => {
        setVariantImages((previousImages) => previousImages.map((image) => {
            if (image.id !== imageIdToUpdate) {
                return image;
            }

            return {
                ...image,
                altText: nextAltText,
                isAltAuto: false,
            };
        }));
    }, []);

    const handleSaveVariant = React.useCallback(async () => {
        const priceCents = parseMoneyToCents(variantPrice);
        const costCents = parseMoneyToCents(variantCost);
        const stockCount = Number.parseInt(String(variantStock).trim(), 10);
        const weightValue = Number(String(variantWeight).trim());
        const localVariantImages = variantImages.filter((image) => image.source === "local" && image.file instanceof File);
        const hasMissingImageAltText = variantImages.some((image) => String(image?.altText || "").trim() === "");

        const nextValidationErrors = [];
        if (!generatedSKU) {
            nextValidationErrors.push("SKU cannot be empty.");
        }

        if (!hasAtLeastOneAttributeValue) {
            nextValidationErrors.push("At least one attribute value is required.");
        }

        if (String(variantPrice).trim() === "" || String(variantCost).trim() === "") {
            nextValidationErrors.push("Please fill all required fields.");
        } else if (Number.isNaN(priceCents) || priceCents < 0 || Number.isNaN(costCents) || costCents < 0) {
            nextValidationErrors.push("Price and cost must be valid non-negative amounts.");
        }

        if (hasMissingImageAltText) {
            nextValidationErrors.push("Each image must have its own alt text.");
        }


        if (nextValidationErrors.length > 0) {
            setSaveVariantErrors(nextValidationErrors);
            return;
        }

        setSaveVariantErrors([]);


        const existingImages = variantImages
            .filter((preview) => preview.source === "existing" && String(preview.url || "").trim())
            .map((preview) => ({
                URL: String(preview.url || "").trim(),
                AltText: String(preview.altText || "").trim(),
            }));

        let localImages = [];
        if (localVariantImages.length > 0) {
            try {
                localImages = await Promise.all(
                    localVariantImages.map(async (entry) => {
                        const dataUrl = await readFileAsDataUrl(entry.file);
                        return {
                            URL: dataUrl,
                            AltText: String(entry.altText || "").trim(),
                        };
                    })
                );
            } catch (error) {
                return;
            }
        }

        const variantObject = {
            ID: 0,
            SKU: generatedSKU,
            Price: priceCents,
            SalePrice: 0,
            EffectivePriceCents: priceCents,
            Cost: costCents,
            Weight: Number.isNaN(weightValue) ? 0 : weightValue,
            Stock: Number.isNaN(stockCount) ? 0 : stockCount,
            Active: true,
            Attributes: normalizedAttributes.map((attribute) => ({
                Name: attribute,
                Value: String(attributeValues[attribute] || "").trim(),
                ...(attribute.trim().toLowerCase() === "color" ? { ColorCode: attributeColors[attribute] || "#000000" } : {}),
            })),
            Images: [...existingImages, ...localImages],
            UploadImages: localVariantImages.map((entry) => ({
                file: entry.file,
                altText: String(entry.altText || "").trim(),
            })),
        };

        onSaveVariant?.(variantObject);
        resetForm();

    }, [attributeColors, attributeValues, generatedSKU, hasAtLeastOneAttributeValue, normalizedAttributes, onSaveVariant, parseMoneyToCents, resetForm, variantCost, variantImages, variantPrice, variantStock, variantWeight]);

    const handleCancelEditing = React.useCallback(() => {
        onCancelEdit?.();
        resetForm();
    }, [onCancelEdit, resetForm]);

    const isSaveDisabled = false;

    return (
        <div className="createVariantModalInnerContainer">
            <div className="createVariantModalHeaderContainer">
                <h1>{isEditing ? "Edit Single Variant" : "Create Single Variant"}</h1>
                <div className="createVariantModalActions">
                    {isEditing ? (
                        <button
                            type="button"
                            className="btn createVariantModalCancelButton"
                            onClick={handleCancelEditing}
                        >
                            Cancel
                        </button>
                    ) : null}
                    <button 
                        type="button"
                        className="btn createVariantModalButton"
                        onClick={handleSaveVariant}
                        disabled={isSaveDisabled}
                    >
                        {isEditing ? "Update Variant" : "Save Variant"} {!isEditing ? <FiPlus size={18}/> : null}
                    </button>
                </div>
            </div>

            <div className="createVariantModalSimpleVariantFormContainer">
                {saveVariantErrors.length > 0 && (
                    <div className="createVariantModalErrorMessage" role="alert" aria-live="polite">
                        {saveVariantErrors.map((errorMessage, index) => (
                            <p key={`${errorMessage}-${index}`}>{errorMessage}</p>
                        ))}
                    </div>
                )}
                <div className="createVariantModalSimpleAttributesContainer">
                    {normalizedAttributes.map((attribute) => (
                            <Input
                                key={attribute}
                                label={attribute}
                                type={attribute.trim().toLowerCase() === "color" ? "textAndColor" : "text"}
                                placeholder={`Enter ${attribute.toLowerCase()}`}
                                styles={attribute.trim().toLowerCase() === "color" ? {} : { control: { background: "var(--page)" } }}
                                value={attributeValues[attribute] || ""}
                                disabled={isEditing}
                                colorValue={attributeColors[attribute] || "#000000"}
                                onColorChange={(event) => setAttributeColors((current) => ({
                                    ...current,
                                    [attribute]: event.target.value,
                                }))}
                                onChange={(e) => {
                                    if (isEditing) {
                                        return;
                                    }

                                    setAttributeValues((previousValues) => ({
                                        ...previousValues,
                                        [attribute]: e.target.value,
                                    }));
                                }}
                            />
                    ))
                    }
                </div>
                <Input 
                    label="Variant SKU"
                    type="text"
                    disabled={true}
                    styles={{ root: { gridColumn: '1 / span 2' } }}
                    value={variantSKU}
                    required={true}
                />

                <Input 
                    label="Price"
                    type="money"
                    placeholder="00.00"
                    value={variantPrice}
                    required={true}
                    onChange={(e) => setVariantPrice(e.target.value)}
                />

                <Input 
                    label="Cost"
                    type="money"
                    placeholder="00.00"
                    value={variantCost}
                    required={true}
                    onChange={(e) => setVariantCost(e.target.value)}
                />

                <Input
                    label="Stock"
                    type="number"
                    placeholder="0" 
                    value={variantStock}
                    required={true}
                    onChange={(e) => setVariantStock(e.target.value)}
                />
                <Input
                    label="Weight (lbs)"
                    type="number"
                    placeholder="0.00"
                    value={variantWeight}
                    required={true}
                    onChange={(e) => setVariantWeight(e.target.value)}
                />

                <div className="createVariantModalImageUploadContainer">
                    <div className="createVariantModalHeaderContainer">
                        <p className="createVariantModalVariantsHeader" style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                            Images
                            <span className="createVariantModalImageCount">{variantImages.length}</span>
                        </p>
                        <button
                            type="button"
                            className="btn createVariantModalButton"
                            style={{ backgroundColor: "var(--surface-2)", color: "var(--fg-default)" }}
                            onClick={() => document.getElementById("variantImageFileInput")?.click()}
                        >
                            Upload Images <LuUpload size={18} />
                        </button>
                    </div>

                    <div className="createVariantModalImageUploadPreview">
                        {variantImages.length > 0 && (
                            <div className="createVariantModalImageUploadPreviewGrid">
                                {variantImages.map((preview) => (
                                    <div key={preview.id} className="createVariantModalImagePreviewCard">
                                        <img {...responsiveMedia(preview.url, "160px")} loading="lazy" decoding="async" alt={preview.altText || "Variant preview"} />
                                        <div className="createVariantModalImagePreviewActions">
                                            <Input 
                                                type="text"
                                                placeholder="Image description"
                                                value={preview.altText || ""}
                                                onChange={(e) => handleImageAltTextChange(preview.id, e.target.value)}
                                                required={true}
                                            />

                                        </div>
                                        <button
                                            type="button"
                                            className="createVariantModalImagePreviewRemoveButton"
                                            onClick={() => handleRemoveImage(preview.id)}
                                        >
                                            <IoClose />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={{ display: "none" }}>
                        <input
                            id="variantImageFileInput"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageFileChange}
                        />

                    </div>
                </div>
            </div>
            
        </div>
    )
}
